import { OfferStatusSchema } from '@ooh/contracts';
import type { Offer } from '@ooh/contracts';

export const OFFER_STATUS_VALUES = OfferStatusSchema.options as readonly Offer['status'][];

/**
 * Visible on ACCEPTED for any caller with effective edit access or
 * offer.approve_any (offer-approval / "Approval Action UI in Offer
 * Workspace"). Also visible on an already-CONFIRMED campaign, but only while
 * `hasUnsecuredAcceptedUnits` is true — the campaign accepted a further
 * version after confirmation and the action now secures the remainder
 * instead of confirming again.
 */
export function canConfirmCampaign(
  status: Offer['status'],
  canEditOffer: boolean,
  canApproveAny: boolean,
  hasUnsecuredAcceptedUnits: boolean,
): boolean {
  if (!canEditOffer && !canApproveAny) {
    return false;
  }
  if (status === 'ACCEPTED') {
    return true;
  }
  return status === 'CONFIRMED' && hasUnsecuredAcceptedUnits;
}

/**
 * Allow-list, never a deny-list: `OfferStatus` already contains `CANCELLED`
 * and more statuses may be added later — a deny-list of the remaining
 * statuses would silently start showing the download action on any new
 * status without this function being touched (offer-order-document-export
 * spec, "Pobierz zamówienie action in the offer workspace"). Gate is offer
 * *status* only, no permission check — matches the endpoint's own gate
 * (read access only, no new permission code). `SOLD` is on the allow-list
 * because the order document is the record of what was sold and stays the
 * document a salesperson re-sends after the sale (offer-order-document /
 * "Pobierz zamówienie action in the offer workspace").
 */
export function canDownloadOrderDocument(status: Offer['status']): boolean {
  return status === 'ACCEPTED' || status === 'CONFIRMED' || status === 'SOLD';
}

/**
 * Visible only on a confirmed campaign, for any caller with effective edit
 * access or offer.approve_any — the same rule that governs campaign
 * confirmation, so a salesperson never learns two different access rules for
 * two consecutive steps (campaign-sale / "Sale action in the offer
 * workspace"). Hidden before confirmation and after the sale is recorded
 * (status moves to SOLD, no longer CONFIRMED).
 */
export function canRecordSale(
  status: Offer['status'],
  canEditOffer: boolean,
  canApproveAny: boolean,
): boolean {
  return status === 'CONFIRMED' && (canEditOffer || canApproveAny);
}

export function getAdminStatusOptions(currentStatus: Offer['status']): Offer['status'][] {
  return OFFER_STATUS_VALUES.filter((status) => status !== currentStatus);
}
